import { REGISTER_PUSH, UNREGISTER_PUSH } from 'src/graphQL/notifications';

const SCOPE = '/universe-push/';
const OWNER_KEY = 'universe.push.owner';
let disconnectPromise;
let sessionClient;
let accountChanged = () => {};
let accountId;

// Wired to the existing Apollo client, and invoked only by JWTContext.
export function configureNotificationSession(client, onAccountChanged) {
  sessionClient = client;
  accountChanged = onAccountChanged;
}

export async function notificationAccountChanged(id) {
  await reconcilePushAccount(id);
  if (accountId !== id) {
    // The first answer of a load that finds nobody signed in changes no
    // account: there is nothing to clear yet, and clearing the store then
    // cancels the query a public page (/files/…) has already sent.
    const anonymousStart = accountId === undefined && id === null;
    accountId = id;
    if (!anonymousStart) await accountChanged();
  }
}

export function pushSupport() {
  const appleMobile =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const installed =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    navigator.standalone;
  if (appleMobile && !installed) return 'install';
  if (
    !window.isSecureContext ||
    !('serviceWorker' in navigator) ||
    !('PushManager' in window) ||
    !('Notification' in window)
  )
    return 'unsupported';
  return Notification.permission === 'denied' ? 'denied' : 'supported';
}

export async function currentPushSubscription() {
  if (!('serviceWorker' in navigator)) return null;
  const registration = await navigator.serviceWorker.getRegistration(SCOPE);
  return registration?.pushManager?.getSubscription() || null;
}

export function pushOwner() {
  try {
    return localStorage.getItem(OWNER_KEY);
  } catch {
    return null;
  }
}
const setOwner = (id) => {
  try {
    if (id) localStorage.setItem(OWNER_KEY, String(id));
    else localStorage.removeItem(OWNER_KEY);
  } catch {
    /* Storage can be disabled; the device is still unsubscribed on logout. */
  }
};

export function vapidBytes(key) {
  const padded = `${key}${'='.repeat((4 - (key.length % 4)) % 4)}`
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  return Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));
}

async function activeRegistration() {
  const registration = await navigator.serviceWorker.register(
    '/universe-push-sw.js',
    { scope: SCOPE, updateViaCache: 'none' }
  );
  if (registration.active) return registration;
  await new Promise((resolve, reject) => {
    const worker = registration.installing || registration.waiting;
    const timer = setTimeout(() => reject(new Error('SW_TIMEOUT')), 15000);
    const change = () => {
      if (worker.state === 'activated') {
        clearTimeout(timer);
        worker.removeEventListener('statechange', change);
        resolve();
      }
      if (worker.state === 'redundant') {
        clearTimeout(timer);
        worker.removeEventListener('statechange', change);
        reject(new Error('SW_FAILED'));
      }
    };
    if (!worker) {
      clearTimeout(timer);
      reject(new Error('SW_FAILED'));
      return;
    }
    worker.addEventListener('statechange', change);
    change();
  });
  return registration;
}

/** Invoke directly from the button handler: Safari requires permission within the user gesture. */
export async function enablePush(client, userId, publicKey) {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted')
    throw new Error(permission === 'denied' ? 'PUSH_DENIED' : 'PUSH_DISMISSED');
  const registration = await activeRegistration();
  let subscription = await registration.pushManager.getSubscription();
  if (subscription && pushOwner() !== String(userId)) {
    await subscription.unsubscribe();
    subscription = null;
  }
  if (!subscription)
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: vapidBytes(publicKey)
    });
  try {
    await client.mutate({
      mutation: REGISTER_PUSH,
      variables: { subscription: subscription.toJSON() }
    });
    setOwner(userId);
    return subscription;
  } catch (error) {
    await subscription.unsubscribe();
    setOwner(null);
    throw error;
  }
}

/** Unsubscribe even when the API is unreachable, so offline logout stops device delivery. */
export async function disconnectPush(client = sessionClient) {
  if (disconnectPromise) return disconnectPromise;
  disconnectPromise = (async () => {
    const subscription = await currentPushSubscription();
    if (subscription) {
      // Queue deletion while the previous authenticated session still exists.
      const removal = client
        ?.mutate({
          mutation: UNREGISTER_PUSH,
          variables: { endpoint: subscription.endpoint }
        })
        .catch(() => null);
      const removed = await subscription.unsubscribe();
      if (!removed && (await currentPushSubscription()))
        throw new Error('PUSH_DISCONNECT_FAILED');
      // A network outage must not trap sign-out after the browser revoked the endpoint.
      if (removal)
        await Promise.race([
          removal,
          new Promise((resolve) => setTimeout(resolve, 1500))
        ]);
    }
    setOwner(null);
  })().finally(() => {
    disconnectPromise = null;
  });
  return disconnectPromise;
}

export async function reconcilePushAccount(userId) {
  if (!userId || pushOwner() !== String(userId)) await disconnectPush();
}
